import { defineComponent as _defineComponent } from 'vue'
import MassEntryTable2 from "@/components/modules/entry/MassEntryTable2.vue";
import EditRowsModal, {
  EditForm,
} from "@/components/modules/entry/EditRowsModal.vue";
import AccountSelectModal from "@/components/modules/account/AccountSelectModal.vue";
import { apolloClient, queries, mutations } from "@/apollo";
import {
  Release,
  AppMode,
  User,
  WindowSettings,
  StoredDdPage,
} from "@/CodegenTypes";
import { Day, EntryInput } from "@/types";
import { parseEntryInputs } from "@/helpers/dd/parseEntryInputs";
import { calculateEntryCost } from "@/helpers/dd/calculateEntryCost";
import { calculateEntries } from "@/helpers/dd/calculateEntries";
import { getValidDayOptions } from "@/helpers/getValidDayOptions";
import { clamp } from "lodash";
import { validateNumber } from "@/helpers/dd/validateNumber";
import { errorMutationNotification } from "@/utils";
import { formatTimeSetting } from "@/helpers/formatTimeSetting";
import { useRootStore } from "@/store/pinia";
import { useTranslation } from "@/store/translation";
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { useRoute } from "vue-router/composables";

type RouteQueryValue = string | (string | null)[] | null | undefined;


export default /*#__PURE__*/_defineComponent({
  __name: 'FixedBet',
  setup(__props) {

const route = useRoute();
const rootStore = useRootStore();
const vm = getCurrentInstance()?.proxy as any;
const { $t } = useTranslation();
const t = (text: string) => $t(text);

const releases = ref<null | Release[]>(null);
const selectedDayIndex = ref(0);
const selectedPageId = ref<null | StoredDdPage["id"]>(null);
const page = ref("");
const showEditModal = ref(false);
const isSubmitting = ref(false);
const showAccountSelectionModal = ref(false);
const editForm = ref({
  rowsFrom: undefined as undefined | number,
  rowsTo: undefined as undefined | number,
  big: undefined as undefined | EntryInput["big"],
  small: undefined as undefined | EntryInput["small"],
  mode: undefined as undefined | EntryInput["mode"],
});
const MAX_ROWS = 60;
const tables = [{ startingIndex: 0 }, { startingIndex: 20 }, { startingIndex: 40 }];
const entries = ref<EntryInput[]>([]);
let entriesInitialized = false;

const selectedAccountId = computed(() => getRouteQueryValue(route.query.userId));
const releasesVariables = computed(() => {
  return {
    appMode: rootStore.appMode,
  };
});
const windowSettingsVariables = computed(() => {
  return {
    appMode: rootStore.appMode as AppMode,
  };
});
const userVariables = computed(() => {
  return {
    userId: selectedAccountId.value,
  };
});
const storedPagesVariables = computed(() => {
  return {
    storedPageType: "fixed",
    includeDownlines: true,
    userId: selectedAccountId.value,
  };
});
const { result: releasesResult, loading: releasesLoading } = useQuery<{
  releases: Release[];
}>(queries.releases, releasesVariables, {
  fetchPolicy: "cache-and-network",
});
const { result: windowSettingsResult, loading: windowSettingsLoading } = useQuery<{
  windowSettings: WindowSettings;
}>(queries.windowSettings, windowSettingsVariables, {
  fetchPolicy: "cache-and-network",
});
const { result: userResult, loading: userLoading } = useQuery<{ user: User }>(
  queries.userWithCreditSummary,
  userVariables,
  {
    enabled: computed(() => !!selectedAccountId.value),
    fetchPolicy: "cache-and-network",
  }
);
const {
  result: storedPagesResult,
  loading: storedPagesLoading,
  refetch: refetchStoredPages,
} = useQuery<{ user: { storedPages: StoredDdPage[] } }>(
  queries.userStoredPages,
  storedPagesVariables,
  {
    enabled: computed(() => !!selectedAccountId.value),
    fetchPolicy: "cache-and-network",
  }
);

const user = computed(() => userResult.value?.user ?? null);
const windowSettings = computed(() => windowSettingsResult.value?.windowSettings ?? null);
const storedPages = computed(() => storedPagesResult.value?.user?.storedPages ?? null);
const apolloLoading = computed(
  () =>
    releasesLoading.value ||
    windowSettingsLoading.value ||
    userLoading.value ||
    storedPagesLoading.value
);
const pageOptions = computed(() => {
  const defaultOption = { label: "-- New --", value: null };

  if (!storedPages.value) {
    return [defaultOption];
  }
  const allOptions = storedPages.value.map((storedPage) => {
    return {
      label: `[${storedPage.userId.toUpperCase()}, ${storedPage.username}] ${storedPage.name}`,
      value: storedPage.id,
    };
  });

  return [defaultOption, ...allOptions];
});
const selectedDay = computed<Day>({
  get() {
    if (releases.value) {
      const selectedOption =
        dayOptions.value[selectedDayIndex.value] ?? dayOptions.value[0];
      return (selectedOption?.value ?? 0) as Day;
    }
    return 0;
  },
  set(val) {
    const index = dayOptions.value.findIndex((opt) => opt.value === val);
    selectedDayIndex.value = index === -1 ? 0 : index;
  },
});
const validEntries = computed(() => parseEntryInputs(entries.value));
const calculations = computed(() => calculateEntries(validEntries.value));
const validDayOptions = computed(() => {
  if (!releases.value) {
    return [];
  }
  return getValidDayOptions(releases.value);
});
const dayOptions = computed(() => {
  const OPTIONS = [
    { label: "7=<Sun>", value: 7, defaultPriority: false },
  ];

  return OPTIONS.filter((o) => validDayOptions.value.includes(o.value));
});
const totalCost = computed(() => {
  const costs = validEntries.value.map((entry) => calculateEntryCost(entry));
  const bigCosts = costs.reduce((sum, cost) => {
    sum += cost.big;
    return sum;
  }, 0);
  const smallCosts = costs.reduce((sum, cost) => {
    sum += cost.small;
    return sum;
  }, 0);

  return {
    big: bigCosts,
    small: smallCosts,
  };
});
const selectedPage = computed(() => {
  if (!storedPages.value) {
    return null;
  }
  return storedPages.value.find((storedPage) => storedPage.id === selectedPageId.value);
});

watch(
  releasesResult,
  (result) => {
    if (result?.releases) {
      releases.value = result.releases;
      initialiseEntries();
    }
  },
  { immediate: true }
);
watch(selectedPage, fillWithPage);
watch(selectedAccountId, resetPage);

onMounted(async () => {
  const result = await apolloClient.query<{ releases: Release[] }>({
    query: queries.releases,
    variables: releasesVariables.value,
  });
  releases.value = result.data.releases;
  initialiseEntries();
});

function getRouteQueryValue(query: RouteQueryValue) {
  return Array.isArray(query) ? query[0] : query;
}

function initialiseEntries() {
  if (!releases.value || entriesInitialized) {
    return;
  }
  const priorityIndex = dayOptions.value.findIndex((opt) => opt.defaultPriority);
  selectedDayIndex.value = priorityIndex === -1 ? 0 : priorityIndex;
  doClearEntries();
  entriesInitialized = true;
}

function fillWithPage() {
  const currentPage = selectedPage.value;
  doClearEntries();
  if (!currentPage) {
    page.value = "";
    return;
  }
  page.value = currentPage.name;

  const newEntries = [...entries.value] as EntryInput[];
  for (const [index, entry] of currentPage.entries.entries()) {
    newEntries[index].number = entry.number;
    newEntries[index].big = String(entry.big);
    newEntries[index].small = String(entry.small);
    newEntries[index].mode = entry.mode || undefined;
  }
  entries.value = newEntries;
}

function resetPage() {
  page.value = "";
  selectedPageId.value = null;
}

function toggleEditModal() {
  showEditModal.value = !showEditModal.value;
}

function focus(field: "number" | "big" | "small", inputTargetIndex: number) {
  let targetIndex = inputTargetIndex;
  if (targetIndex < 0) {
    targetIndex = 59;
  } else if (targetIndex > 59) {
    targetIndex = 0;
  }
  const tablesEles = vm.$refs["mass-entry-table"] as any;
  for (const [i, table] of tables.entries()) {
    const startingIndex = table.startingIndex;
    if (targetIndex >= startingIndex && targetIndex < startingIndex + 20) {
      tablesEles[i].focus(field, targetIndex);
    }
  }
  return;
}

function performEditRows(newEditForm: EditForm) {
  if (!newEditForm.rowsFrom || !newEditForm.rowsTo) {
    return;
  }

  const rowsFrom = clamp(newEditForm.rowsFrom, 0, MAX_ROWS);
  const rowsTo = clamp(newEditForm.rowsTo, 0, MAX_ROWS);
  const range = [rowsFrom, rowsTo].sort((a, b) => a - b);

  for (let i = range[0] - 1; i <= range[1] - 1; i++) {
    entries.value[i].big = newEditForm.big;
    entries.value[i].small = newEditForm.small;
    entries.value[i].mode = newEditForm.mode;
  }
  editForm.value = {
    rowsFrom: undefined,
    rowsTo: undefined,
    big: undefined,
    small: undefined,
    mode: undefined,
  };
  showEditModal.value = false;
}

async function clearEntries(event: Event) {
  vm.$confirm.require({
    target: event.currentTarget,
    group: "popups",
    header: "Clear the form?",
    message: "All existing inputs will be deleted.",
    icon: "pi pi-exclamation-triangle",
    accept: () => {
      doClearEntries();
    },
    reject: () => {
      // Optional: Handle rejection
    },
  });
}

function doClearEntries() {
  const newEntries = [];

  for (let i = 0; i < MAX_ROWS; i++) {
    newEntries.push({
      day: selectedDay.value,
      number: "",
      big: undefined,
      small: undefined,
      mode: undefined,
    });
  }
  entries.value = newEntries;
}

function validateAllAndShowError() {
  const errors = [];
  for (const [index, entry] of entries.value.entries()) {
    const validNumber = validateNumber(entry);
    const noSmall = (entry.small as any) === "" || entry.small === undefined;
    const noBig = (entry.big as any) === "" || entry.big === undefined;
    const hasMode = entry.mode;
    if (entry.number === "" && noSmall && noBig && !hasMode) {
      continue;
    }
    if (!validNumber) {
      errors.push(`Row ${index + 1} has invalid number`);
    }
    if (validNumber && noSmall && noBig) {
      errors.push(`Row ${index + 1} has no big and small`);
    }
  }
  return errors;
}

async function handleDelete() {
  if (!selectedAccountId.value) {
    vm.$toast.add({
      severity: "error",
      summary: "Error",
      detail: "No account selected",
      life: 3000,
    });
    return;
  }

  vm.$confirm.require({
    header: "Confirm?",
    message: t("Are you sure to delete the fixed bet?"),
    icon: "pi pi-exclamation-triangle",
    accept: async () => {
      try {
        isSubmitting.value = true;
        await apolloClient.mutate({
          mutation: mutations.deleteSavedDdPage,
          variables: {
            id: selectedPage.value?.id,
          },
        });
        await refetchStoredPages(storedPagesVariables.value);

        vm.$toast.add({
          severity: "success",
          summary: "Success",
          detail: "Fixed bet deleted.",
          life: 3000,
        });
      } catch (err) {
        errorMutationNotification(err);
      } finally {
        isSubmitting.value = false;
      }
    },
    reject: () => {
      // Optional: Handle rejection
    },
  });
}

async function handleSave() {
  const errors = validateAllAndShowError();
  if (errors.length) {
    vm.$toast.add({
      severity: "error",
      summary: "Error",
      detail: errors[0],
      life: 3000,
    });
    return;
  }

  if (!selectedAccountId.value) {
    vm.$toast.add({
      severity: "error",
      summary: "Error",
      detail: "No account selected",
      life: 3000,
    });
    return;
  }

  const parsedEntries = validEntries.value;

  if (!parsedEntries.length) {
    vm.$toast.add({
      severity: "error",
      summary: "Error",
      detail: "Please enter number details for entries",
      life: 3000,
    });
    return;
  }

  vm.$confirm.require({
    header: "Confirm?",
    message: t("Are you sure to save the entries for Fixed betting?"),
    icon: "pi pi-exclamation-triangle",
    accept: async () => {
      try {
        isSubmitting.value = true;
        const { data } = await apolloClient.mutate({
          mutation: mutations.saveDdEntry,
          variables: {
            page: {
              id: selectedPage.value?.id || undefined,
              page: page.value,
              entries: parsedEntries,
              userId: selectedAccountId.value,
              storedPageType: "fixed",
            },
          },
        });
        selectedPageId.value = data.saveDdEntry;
        await refetchStoredPages(storedPagesVariables.value);

        vm.$toast.add({
          severity: "success",
          summary: "Success",
          detail: "Entry saved successfully.",
          life: 3000,
        });

        fillWithPage();
      } catch (err) {
        errorMutationNotification(err);
      } finally {
        isSubmitting.value = false;
      }
    },
    reject: () => {
      // Optional: Handle rejection
    },
  });
}

return { __sfc: true,route, rootStore, vm, $t, t, releases, selectedDayIndex, selectedPageId, page, showEditModal, isSubmitting, showAccountSelectionModal, editForm, MAX_ROWS, tables, entries, entriesInitialized, selectedAccountId, releasesVariables, windowSettingsVariables, userVariables, storedPagesVariables, releasesResult, releasesLoading, windowSettingsResult, windowSettingsLoading, userResult, userLoading, storedPagesResult, storedPagesLoading, refetchStoredPages, user, windowSettings, storedPages, apolloLoading, pageOptions, selectedDay, validEntries, calculations, validDayOptions, dayOptions, totalCost, selectedPage, getRouteQueryValue, initialiseEntries, fillWithPage, resetPage, toggleEditModal, focus, performEditRows, clearEntries, doClearEntries, validateAllAndShowError, handleDelete, handleSave, MassEntryTable2, EditRowsModal, AccountSelectModal, formatTimeSetting }
}

})